// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/user/components/AvatarDecorationOverlay.module.css';
import {getCosmeticPoster} from '@app/features/user/state/CosmeticPosterRegistry';
import {getAvatarDecorationMediaKind} from '@fluxer/schema/src/domains/user/AvatarDecorationSchemas';
import {useEffect, useRef, useState} from 'react';

interface AvatarDecorationMediaProps {
	src: string;
	className?: string;
	active?: boolean;
}

export function AvatarDecorationMedia({src, className, active = true}: AvatarDecorationMediaProps) {
	// Discord-style: show the tiny static poster until first activation; only then download the animation.
	const poster = getCosmeticPoster(src);
	const [wasActive, setWasActive] = useState(active);
	if (active && !wasActive) setWasActive(true);
	if (poster && !wasActive) {
		return <img src={poster} className={className ?? styles.image} alt="" draggable={false} decoding="async" />;
	}
	const kind = getAvatarDecorationMediaKind(src);
	if (kind === 'video') {
		return <AvatarDecorationVideo src={src} className={className} active={active} />;
	}
	if (kind === 'image') {
		return <AvatarDecorationImage src={src} className={className} active={active} />;
	}
	return null;
}

function AvatarDecorationVideo({src, className, active = true}: AvatarDecorationMediaProps) {
	const videoRef = useRef<HTMLVideoElement>(null);
	useEffect(() => {
		const video = videoRef.current;
		if (!video) {
			return;
		}
		video.currentTime = 0;
		if (active) {
			void video.play().catch(() => undefined);
			return;
		}
		video.pause();
	}, [active, src]);
	return (
		<video
			ref={videoRef}
			className={className ?? styles.image}
			src={src}
			muted
			loop
			playsInline
			autoPlay={active}
			preload="auto"
			aria-hidden="true"
		/>
	);
}

function AvatarDecorationImage({src, className, active = true}: AvatarDecorationMediaProps) {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const capturedSrcRef = useRef<string | null>(null);
	const [playbackKey, setPlaybackKey] = useState(0);
	const wasActiveRef = useRef(active);
	const captureFirstFrame = (img: HTMLImageElement) => {
		if (capturedSrcRef.current === src || img.naturalWidth === 0) {
			return;
		}
		const canvas = canvasRef.current;
		if (!canvas) {
			return;
		}
		canvas.width = img.naturalWidth;
		canvas.height = img.naturalHeight;
		const context = canvas.getContext('2d');
		if (!context) {
			return;
		}
		context.clearRect(0, 0, canvas.width, canvas.height);
		context.drawImage(img, 0, 0);
		capturedSrcRef.current = src;
	};
	useEffect(() => {
		capturedSrcRef.current = null;
	}, [src]);
	useEffect(() => {
		if (wasActiveRef.current && !active) {
			setPlaybackKey((key) => key + 1);
		}
		wasActiveRef.current = active;
	}, [active]);
	return (
		<>
			<img
				src={src}
				alt=""
				draggable={false}
				aria-hidden="true"
				onLoad={(event) => captureFirstFrame(event.currentTarget)}
				style={{position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none'}}
			/>
			<canvas
				ref={canvasRef}
				className={className ?? styles.image}
				aria-hidden="true"
				style={{display: active ? 'none' : 'block'}}
			/>
			{active ? (
				<img
					key={`${src}:${playbackKey}`}
					className={className ?? styles.image}
					src={src}
					alt=""
					draggable={false}
				/>
			) : null}
		</>
	);
}
